import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ShoppingCart, Package, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { newEquipment } from '../data/mock';
import PurchaseModal from './PurchaseModal';

const NewEquipmentSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const categories = ['Todos', ...new Set(newEquipment.map(p => p.category))];
  
  const filteredProducts = selectedCategory === 'Todos' 
    ? newEquipment 
    : newEquipment.filter(p => p.category === selectedCategory);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleBuyProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    }
  };

  return (
    <section className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600/20 border border-violet-500/30 rounded-full mb-6"
          >
            <Sparkles className="w-4 h-4 text-violet-400" />
            <span className="text-violet-300 text-sm font-mono tracking-wider uppercase">Nuevo</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Equipos Nuevos
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            La última tecnología en hardware, consolas y realidad virtual
          </motion.p>
        </div>

        {/* Category Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 mb-10"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-mono tracking-wide transition-all
                ${selectedCategory === category 
                  ? 'bg-violet-600 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-violet-900/30'
                }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredProducts.map((product) => (
            <motion.div 
              key={product.id} 
              variants={itemVariants}
              whileHover={{ y: -4 }}
              className="cursor-pointer"
              onClick={() => handleBuyProduct(product)}
            >
              <Card className={`bg-white/5 border-violet-900/30 hover:border-violet-500/50 transition-all h-full flex flex-col relative overflow-hidden hover:shadow-lg hover:shadow-violet-500/10
                ${product.featured ? 'ring-1 ring-violet-500/50' : ''}`}
              >
                {/* Product Image */}
                <div className="relative h-48 overflow-hidden bg-black/50">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-violet-600/90 text-white rounded-full text-xs font-mono backdrop-blur-sm">
                      {product.category}
                    </span>
                  </div>
                  {product.featured && (
                    <div className="absolute top-3 right-3">
                      <span className="flex items-center gap-1 px-2 py-1 bg-violet-600 text-white rounded-full text-xs font-mono">
                        <Star className="w-3 h-3 fill-current" />
                        Destacado
                      </span>
                    </div>
                  )}
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">{product.name}</CardTitle>
                  <CardDescription className="text-gray-500">
                    {product.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-violet-400">
                      {formatPrice(product.price)}
                    </span>
                    <span className="flex items-center gap-1 text-gray-500 text-sm">
                      <Package className="w-4 h-4" />
                      Stock: {product.stock}
                    </span>
                  </div>
                  <p className="text-gray-600 text-xs mt-2 font-mono">{product.brand}</p>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBuyProduct(product);
                    }}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full font-mono text-xs tracking-wider uppercase flex items-center justify-center gap-2"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Comprar
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-violet-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-violet-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Próximamente</h3>
            <p className="text-gray-400">Estamos preparando nuevos productos para vos</p>
          </motion.div>
        )}
      </div>

      {/* Purchase Modal */}
      <PurchaseModal 
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default NewEquipmentSection;
